<template>
    <form
        v-if="panelType === 'select'"
        @submit.prevent="submitForm"
        class="d-flex flex-column form-container"
    >
        <!-- Audit Type -->
        <Field
            label="Type"
            v-model="localAudit.audit_type"
            type="radio"
            :required="true"
            :params="{
                options: [
                    { label: 'Audit', value: 'AUDIT' },
                    { label: 'Assessment', value: 'ASSESSMENT' },
                ],
                name: 'audit-type',
            }"
        />

        <!-- Template -->
        <Field
            label="Audit Template"
            v-model="localAudit.template_id"
            type="select"
            :required="true"
            :params="{ options: filteredAuditTypes }"
        />

        <!-- Date -->
        <Field
            label="Date"
            v-model="localAudit.date"
            type="date"
            :required="true"
            :params="{ maxDate: today }"
        />

        <!-- Auditor -->
        <Field
            label="Auditor"
            v-model="localAudit.auditor_id"
            :required="true"
            :disabled="!isAdminRoute"
            type="select"
            :params="{
                options: userOptions,
                placeholder: 'Select an auditor',
                searchable: true,
                allowEmpty: true,
            }"
        />

        <!-- Site -->
        <SiteDropdownField
            v-if="localAudit?.template_id"
            label="Site"
            type="select"
            v-model="localAudit.site_id"
            :required="true"
            :options="siteOptions"
            :disabled="isSiteFieldDisabled"
            :disabled-tooltip="disabledSiteTooltip"
            :placeholder="
                siteOptions.length > 0
                    ? 'Select a site'
                    : 'No sites available'
            "
        >
            <span class="text-muted" style="font-size: 10pt">
                Only users assigned to this site may view this audit
            </span>
        </SiteDropdownField>

        <!-- Area -->
        <div v-if="localAudit?.template_id">
            <div class="area-container">
                <div style="flex: 1">
                    <Field
                        label="Area"
                        v-model="localAudit.area_id"
                        :required="true"
                        :disabled="areaOptions.length == 1"
                        type="select"
                        :params="{
                            options: areaOptions,
                            placeholder: 'Select an area',
                            searchable: true,
                            allowEmpty: true,
                        }"
                        style="width: 100%"
                    />
                </div>
                <button
                    v-if="
                        areaOptions.length === 0 ||
                        (localAudit?.template_id &&
                            (!auditAreas || auditAreas.length === 0))
                    "
                    type="button"
                    class="btn btn-link p-0 area-edit-link"
                    @click="openAreaForm"
                >
                    Edit
                </button>
            </div>

            <span
                v-if="auditAreas?.length > 0"
                class="text-muted"
                style="font-size: 10pt"
            >
                These are the areas added to this template
            </span>
        </div>

        <!-- Save Button -->
        <div
            style="
                padding-top: var(--space-6);
                border-top: 1px solid var(--neutral-600);
            "
        >
            <button type="submit" class="btn btn-secondary w-100">Start</button>
        </div>
    </form>
    <form v-else ref="auditForm" novalidate @submit.prevent="saveAudit">
        <div class="d-flex flex-column gap-3">
            <div class="audit-title">
                <img
                    v-if="localAudit.company_id == 0"
                    src="@/assets/images/logos/logomark.svg"
                    alt="IPAC Logo"
                    style="width: 30px; height: 30px"
                />
                <h3 style="color: var(--primary-500)">{{ localAudit.name }}</h3>
            </div>
            <p v-if="localAudit.description" class="text-muted m-0">
                {{ localAudit.description }}
            </p>
        </div>
        <div class="sticky-header">
            <TabGroup v-model="activeTab" :tabs="tabs" />
        </div>

        <template v-for="(fields, tabName) in fieldsBySection" :key="tabName">
            <div v-show="activeTab === tabName" :data-tab="tabName">
                <div
                    class="d-flex flex-column mt-4"
                    style="gap: var(--space-5)"
                >
                    <div
                        v-for="field in fields"
                        :key="field.id"
                        style="padding-left: 2px"
                    >
                        <FieldBuilder
                            :field="field"
                            :isBuilding="false"
                            :disabled="!canEdit"
                            :panel-id="panelId"
                            @update:field="updateAudit"
                        />
                    </div>
                </div>
            </div>
        </template>
    </form>
</template>

<script>
import Field from '../Field.vue';
import FieldBuilder from '../FieldBuilder.vue';
import TabGroup from '../TabGroup.vue';
import SiteDropdownField from '../fields/SiteDropdownField.vue';
import { useSiteStore } from '@/stores/site';
import { useAreaStore } from '@/stores/area';
import { getDefaultSitesForNewRecord } from '@/utils/SiteAccessUtils';

export default {
    name: 'AuditTemplateContent',
    components: {
        Field,
        FieldBuilder,
        TabGroup,
        SiteDropdownField,
    },
    props: {
        audit: {
            type: Object,
            default: null,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        userOptions: {
            type: Array,
            required: true,
        },
        panelId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            panelType: 'select',
            localAudit: {
                name: '',
                description: '',
                company_id: null,
                date: null,
                auditor_id: this.$userStore.user.id,
                site_id: null,
                area_id: null,
                template_id: null,
                audit_type: 'AUDIT',
            },
            auditId: null,
            auditTypes: [],
            auditAreas: [],
            areaStore: null,
            auditSites: [],
            siteStore: null,
            fieldsBySection: {
                section1: [],
            },
            tabs: [
                {
                    text: 'Section 1',
                    value: 'section1',
                    isEditing: false,
                    hasError: false,
                },
            ],
            activeTab: 'section1',
        };
    },
    emits: ['submit', 'started', 'areas'],
    computed: {
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        today() {
            return new Date();
        },
        filteredAuditTypes() {
            const { audit_type } = this.localAudit;
            if (!audit_type) return this.auditTypes;

            return this.auditTypes.filter(
                ({ supported_audit_types }) =>
                    supported_audit_types &&
                    supported_audit_types === audit_type
            );
        },
        areaOptions() {
            if (this.auditAreas?.length > 0) {
                return this.auditAreas;
            }

            if (this.areaStore) {
                return (this.areaStore?.getAreaOptions || []).map((opt) => ({
                    label: opt.label ?? opt.text,
                    value: opt.value,
                }));
            }
            return [];
        },
        siteOptions() {
            const storeOptions = this.siteStore?.getSiteOptions || [];
            const auditIds = new Set(
                this.auditSites?.map((s) => Number(s.value)) || []
            );
            const userIds = new Set(
                this.$siteStore?.userSiteIds?.map((id) => Number(id)) || []
            );

            return storeOptions.filter((opt) => {
                const id = Number(opt.value);

                // must be in auditSites if any are defined
                const auditOk = auditIds.size === 0 || auditIds.has(id);

                // must be in userSites if not admin and any are defined
                const userOk =
                    this.isAdminRoute || userIds.size === 0 || userIds.has(id);

                return auditOk && userOk;
            });
        },
        // Default site ID based on view type (matches ScheduleAuditForm pattern)
        defaultSiteId() {
            // For existing audits, use their site_id
            if (this.audit?.site_id) {
                return this.audit.site_id;
            }

            // For new audits, use view-based defaults
            const defaultSites = getDefaultSitesForNewRecord({
                viewType: this.$siteStore?.viewType,
                globalSiteId: this.$siteStore?.globalSiteId,
                userSites: this.$siteStore?.userSiteIds || [],
            });

            // Return first site (single-select)
            return defaultSites.length > 0 ? defaultSites[0] : '';
        },
        // Determine if site field should be disabled
        isSiteFieldDisabled() {
            // Disable in selected-site view (library pattern)
            if (this.$siteStore?.isSelectedSiteView) {
                return true;
            }
            // Disable if only one or no sites available
            return this.siteOptions.length <= 1;
        },
        disabledSiteTooltip() {
            if (this.$siteStore?.isSelectedSiteView) {
                return 'To edit the site, change the view from site view to global view';
            }
            if (this.siteOptions.length === 1) {
                return 'Only one site available';
            }
            return '';
        },
    },
    methods: {
        openAreaForm() {
            this.$emit('areas');
        },
        backToSelection() {
            this.panelType = 'select';
            // Reload templates if not already loaded
            if (this.auditTypes.length === 0) {
                this.loadAuditTemplates();
            }
        },
        loadAuditTemplates() {
            const params = {
                fields: ['id', 'name', 'supported_audit_types'],
                order_by: { name: 'asc' },
            };
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success) {
                    const templates = response.data || [];
                    this.auditTypes = templates.map((template) => ({
                        label: template.name,
                        value: String(template.id),
                        supported_audit_types: template.supported_audit_types,
                    }));
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        loadAuditTemplate() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
            };
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, name, description, company_id, form_json } =
                        response.data?.[0];
                    // Calculate total max_score across all sections
                    const totalMaxScore = Array.isArray(form_json)
                        ? form_json.reduce(
                              (sum, page) => sum + Number(page.max_score ?? 0),
                              0
                          )
                        : 0;

                    this.localAudit.name = name || '';
                    this.localAudit.description = description || '';
                    this.localAudit.company_id = company_id ?? null;
                    this.localAudit.form_max_score = totalMaxScore || null;
                    this.populateFromFormJson(form_json);
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to load template'
                    );
                }
            });
        },
        loadAuditTemplateOptions() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
                fields: ['id'],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    areas: {
                        fields: ['id', 'name'],
                    },
                },
            };
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, sites, areas } = response.data?.[0];

                    const siteOptions =
                        sites?.map((s) => ({
                            label: s.name,
                            value: String(s.id),
                        })) || [];

                    const areaOptions =
                        areas?.map((a) => ({
                            label: a.name,
                            value: String(a.id),
                        })) || [];

                    this.auditSites = siteOptions;
                    this.auditAreas = areaOptions;
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to load template'
                    );
                }
            });
        },
        populateFromFormJson(form_json) {
            this.tabs = form_json.map((section, index) => ({
                text: section.heading,
                value: `section${index + 1}`,
                isEditing: false,
                hasError: false,
            }));

            this.fieldsBySection = Object.fromEntries(
                form_json.map((section, index) => [
                    `section${index + 1}`,
                    section.fields.map((field) => ({
                        ...field,
                        isEditing: false,
                    })),
                ])
            );
            this.activeTab = this.tabs.length ? this.tabs[0].value : '';
        },
        updateAudit(updatedField) {
            for (const sectionKey in this.fieldsBySection) {
                const sectionArray = this.fieldsBySection[sectionKey];
                const idx = sectionArray.findIndex(
                    (f) => f.id === updatedField.id
                );
                if (idx !== -1) {
                    Object.assign(sectionArray[idx], updatedField);
                    break;
                }
            }
        },
        submitForm() {
            this.loadAuditTemplate();
            this.$emit('started', this.localAudit.audit_type);
            this.panelType = 'create';
        },
        getFormattedFormJson() {
            return this.tabs.map((tab) => ({
                heading: tab.text,
                fields: this.fieldsBySection[tab.value].map((field) => {
                    const { isEditing, ...cleanField } = field;
                    if (Array.isArray(cleanField.conditionals)) {
                        cleanField.conditionals.forEach((cond) => {
                            if (
                                cleanField.value !== cond.value &&
                                cond.field?.value !== undefined
                            ) {
                                cond.field.value = '';
                            }
                        });
                    }
                    return cleanField;
                }),
            }));
        },
        handleInvalidInputs() {
            const form = this.$refs.auditForm;
            const firstInvalid = form.querySelector(':invalid');
            if (!firstInvalid) return;

            let el = firstInvalid;
            while (el && !el.dataset.tab) {
                el = el.parentNode;
            }
            if (el && el.dataset.tab) {
                this.activeTab = el.dataset.tab;
                firstInvalid.focus();
            }
        },
        updateTabErrors() {
            const form = this.$refs.auditForm;
            const invalidFields = form.querySelectorAll(':invalid');

            this.tabs.forEach((tab) => {
                tab.hasError = false;
            });

            invalidFields.forEach((field) => {
                let el = field;
                while (el && !el.dataset.tab) {
                    el = el.parentNode;
                }
                if (el && el.dataset.tab) {
                    const tab = this.tabs.find(
                        (t) => t.value === el.dataset.tab
                    );
                    if (tab) tab.hasError = true;
                }
            });
        },
        saveAudit() {
            const form = this.$refs.auditForm;

            if (!form.checkValidity()) {
                this.updateTabErrors();
                form.reportValidity();
                this.handleInvalidInputs();
                return;
            }

            // Calculate total score and max possible across all tabs
            let totalScore = 0;
            let totalMaxScore = 0;

            this.tabs.forEach((tab) => {
                const fields = this.fieldsBySection[tab.value];
                const result = this.calculateScoreForFields(fields);
                totalScore += result.totalScore;
                totalMaxScore += result.totalMaxPossible;
            });

            // Remove company_id from the audit data while saving
            const { company_id, ...auditData } = this.localAudit;

            const new_audit = {
                ...auditData,
                form_json: this.getFormattedFormJson(),
                form_score: totalScore || 0,
                form_max_score: totalMaxScore || null,
            };

            if (this.auditId) {
                new_audit.id = this.auditId;
            }

            if (!this.audit?.completed_at) {
                new_audit.completed_at = Date.now();
            }

            if (this.$route.matched.some((r) => r.name === 'Admin')) {
                new_audit.app_admin_only = true;
            }
            const endpoint = this.auditId ? 'audit/update' : 'audit/create';
            this.$postData(endpoint, new_audit).then((response) => {
                if (response?.success) {
                    this.$emit('submit');
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        calculateScoreForFields(fields) {
            let totalScore = 0;
            let totalMaxPossible = 0;

            fields.forEach((field) => {
                const { value, scoring_rules, max_score, timer, threshold, params } =
                    field;

                if (!max_score) return;

                const values = Array.isArray(value) ? value : [value];
                
                // Check if any selected value is marked as NA in the options
                const isNA = values.some(v => {
                    const opt = params?.options?.find(o => o.value === v);
                    return opt?.isNA === true;
                });

                if (isNA) return; // Skip scoring and max possible score for this field

                totalMaxPossible += Number(max_score);

                if (!Array.isArray(scoring_rules) || scoring_rules.length === 0)
                    return;

                const rules = [...scoring_rules];
                const isCorrect = values.some((v) => rules.includes(v));

                if (!isCorrect) return;

                const maxScore = Number(max_score ?? 0);

                // If there's a threshold, they must beat it to score
                if (threshold) {
                    const thresholdNum = Number(threshold);
                    if (thresholdNum > 0) {
                        const halfScore = maxScore / 2;
                        const timerValue = Number(timer ?? 0);
                        const timePercentage = Math.min(timerValue / thresholdNum, 1);
                        const timerScore = timePercentage * halfScore;
                        
                        totalScore += (halfScore + timerScore);
                    } else {
                        totalScore += maxScore;
                    }
                } else {
                    // No threshold → normal correct-answer scoring
                    totalScore += maxScore;
                }
            });

            // Round the final sum to 2 decimal places to guarantee accuracy for the database
            return {
                totalScore: Math.round(totalScore * 100) / 100,
                totalMaxPossible,
            }
        },
    },
    watch: {
        canEdit(newVal) {
            if (this.auditId) {
                this.panelType = newVal ? 'edit' : 'view';
            }
        },
        siteOptions(newVal) {
            // If no site selected yet and options are available
            if (!this.localAudit.site_id && newVal.length > 0) {
                // Check if defaultSiteId is in available options
                const defaultId = this.defaultSiteId;
                const isDefaultAvailable = newVal.some(
                    (opt) => opt.value === defaultId
                );

                if (isDefaultAvailable) {
                    // Use view-based default if available
                    this.localAudit.site_id = defaultId;
                } else if (newVal.length === 1) {
                    // Otherwise auto-select if only one option
                    this.localAudit.site_id = newVal[0].value;
                }
            }
        },
        areaOptions(newVal) {
            if (newVal.length === 1) {
                this.localAudit.area_id = newVal[0].value;
            }
        },
        'localAudit.template_id'(newVal) {
            if (newVal) {
                if (this.panelType == 'select') {
                    this.localAudit.area_id = null;
                    this.localAudit.site_id = null;
                    this.loadAuditTemplateOptions();
                }
            }
        },
    },
    created() {
        this.siteStore = useSiteStore();
        this.siteStore.syncSites();
        this.areaStore = useAreaStore();
        this.areaStore.syncAreas();
    },
    mounted() {
        if (this.audit && Object.keys(this.audit).length > 0) {
            this.auditId = this.audit.id || null;
            this.localAudit = {
                name: this.audit.name || '',
                description: this.audit.description || '',
                company_id: this.audit.company_id ?? null,
                date: this.audit.date || null,
                auditor_id: this.audit.auditor_id || null,
                site_id: this.audit.site_id || null,
                area_id: this.audit.area_id || null,
                template_id: this.audit.template_id || null,
                audit_type: this.audit.audit_type || 'AUDIT',
                form_max_score: this.audit.form_max_score || null,
            };
            if (this.audit?.score_override) {
                this.localAudit.score_override = this.audit.score_override;
            } else {
                this.localAudit.score_override = null;
            }
            if (
                !this.isAdminRoute &&
                !this.audit &&
                Object.keys(this.audit).length == 0
            ) {
                this.localAudit.auditor_id = this.$userStore.user_id;
            }
            this.panelType = this.canEdit ? 'edit' : 'view';
            if (this.audit?.form_json) {
                this.populateFromFormJson(this.audit.form_json);
            } else {
                this.loadAuditTemplate();
            }
        } else {
            this.loadAuditTemplates();
            this.localAudit.date = new Date().toISOString().split('T')[0];
        }
    },
};
</script>

<style scoped>
.audit-title {
    background-color: var(--primary-25);
    display: flex;
    padding: var(--Spacing-space-2, 8px) var(--Spacing-space-4, 16px);
    align-items: center;
    gap: 10px;
    align-self: stretch;
}

.sticky-header {
    position: sticky;
    top: 0;
    /* stick to the top of the scroll container */
    background: white;
    margin-top: 1rem;
    z-index: 10;
}

.tab-hidden {
    visibility: hidden;
    position: absolute;
    height: 0;
    overflow: hidden;
}

.edit-area,
.area-edit-link {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    /* 120% */
    text-decoration-line: underline;
    white-space: nowrap;
}

.form-container {
    gap: var(--space-4);
}

.area-edit-link:hover {
    color: var(--primary-400, #678277);
    background-color: transparent;
}

.area-container {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
}

.area-container .area-edit-link {
    margin-bottom: 0.625rem;
}
</style>
